import React, { useState, useEffect, useRef } from 'react';
import {
    FileUp, FileType2, FileSpreadsheet, FileText, FileJson,
    ZoomIn, ZoomOut, Maximize, Minimize, Printer
} from 'lucide-react';
import Tooltip from './Tooltip';
// Import export libraries
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import htmlDocx from 'html-docx-js/dist/html-docx';
import { saveAs } from 'file-saver';
// Import professional export utilities
import { exportSoalToWord, exportSoalToPdf } from '../../utils/exportUtils';

/**
 * OutputToolbar Component
 * Toolbar untuk mengelola output yang dihasilkan (export, import, zoom, fullscreen)
 */
const OutputToolbar = ({
    generatedData,
    onImportJson,
    getHtmlContent,
    getTextContent,
    targetRef,
    fileName = 'output',
    // New props for professional export
    soalData = null,
    metadata = {}
}) => {
    const [fontSize, setFontSize] = useState(14);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (targetRef?.current) {
            targetRef.current.style.fontSize = `${fontSize}px`;
        }
    }, [fontSize, targetRef]);

    const handleExport = async (format) => {
        if (!generatedData && !targetRef?.current && !soalData) return;

        let blob;
        let mimeType;
        let filename = `${fileName}.${format}`;

        if (format === 'json') {
            mimeType = 'application/json';
            blob = new Blob([JSON.stringify(generatedData, null, 2)], { type: mimeType });
        } else if (format === 'txt') {
            mimeType = 'text/plain;charset=utf-8';
            const textContent = getTextContent ? getTextContent() : targetRef.current?.innerText || '';
            blob = new Blob([textContent], { type: mimeType });
        } else if (format === 'doc') {
            // Use professional export if soalData is available
            if (soalData && soalData.length > 0) {
                await exportSoalToWord(soalData, metadata, fileName);
                return;
            }

            // Fallback: Use htmlDocx for non-soal content
            const content = getHtmlContent ? getHtmlContent() : targetRef.current?.innerHTML || '';
            const htmlTemplate = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="utf-8">
                    <style>
                        body { font-family: 'Times New Roman', serif; font-size: 12pt; line-height: 1.5; }
                        h1, h2, h3 { font-weight: bold; }
                        .soal-item { margin-bottom: 1em; }
                        .pilihan { margin-left: 1em; }
                        .jawaban { font-weight: bold; color: green; }
                    </style>
                </head>
                <body>${content}</body>
                </html>
            `;
            try {
                const converted = htmlDocx.asBlob(htmlTemplate);
                filename = `${fileName}.docx`;
                saveAs(converted, filename);
                return;
            } catch (error) {
                console.error('Error exporting to Word:', error);
                alert('Gagal mengekspor ke Word. Silakan coba lagi.');
                return;
            }
        } else if (format === 'pdf') {
            // Use professional export if soalData is available
            if (soalData && soalData.length > 0) {
                await exportSoalToPdf(soalData, metadata, fileName);
                return;
            }

            // Fallback: Use html2canvas for non-soal content
            const content = targetRef.current;
            if (content) {
                const wasHidden = content.style.display === 'none';
                if (wasHidden) content.style.display = 'block';

                html2canvas(content, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff'
                }).then(canvas => {
                    if (wasHidden) content.style.display = 'none';
                    const imgData = canvas.toDataURL('image/png');
                    const pdf = new jsPDF('p', 'mm', 'a4');
                    const pdfWidth = pdf.internal.pageSize.getWidth();
                    const pdfHeight = pdf.internal.pageSize.getHeight();
                    const canvasWidth = canvas.width;
                    const canvasHeight = canvas.height;
                    const ratio = canvasWidth / canvasHeight;
                    let width = pdfWidth - 20; // with margin
                    let height = width / ratio;

                    // Handle multi-page PDF for long content
                    const pageHeight = pdfHeight - 20;
                    let position = 10;

                    if (height > pageHeight) {
                        // Content is longer than one page
                        const heightLeft = canvasHeight;
                        const pageCount = Math.ceil(height / pageHeight);

                        for (let i = 0; i < pageCount; i++) {
                            if (i > 0) {
                                pdf.addPage();
                            }
                            const srcY = (i * pageHeight / height) * canvasHeight;
                            const srcH = Math.min((pageHeight / height) * canvasHeight, canvasHeight - srcY);

                            // Create a canvas for this page portion
                            const pageCanvas = document.createElement('canvas');
                            pageCanvas.width = canvasWidth;
                            pageCanvas.height = srcH;
                            const ctx = pageCanvas.getContext('2d');
                            ctx.drawImage(canvas, 0, srcY, canvasWidth, srcH, 0, 0, canvasWidth, srcH);

                            const pageImgData = pageCanvas.toDataURL('image/png');
                            const pageRatio = canvasWidth / srcH;
                            const pageW = pdfWidth - 20;
                            const pageH = pageW / pageRatio;

                            pdf.addImage(pageImgData, 'PNG', 10, 10, pageW, Math.min(pageH, pageHeight));
                        }
                    } else {
                        pdf.addImage(imgData, 'PNG', 10, position, width, height);
                    }

                    pdf.save(filename);
                }).catch(error => {
                    console.error('Error generating PDF:', error);
                    alert('Gagal membuat PDF. Silakan coba lagi.');
                });
            }
            return;
        }

        if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    };

    // Print function - opens browser print dialog
    const handlePrint = () => {
        const content = targetRef?.current;
        if (!content) {
            alert('Tidak ada konten untuk dicetak.');
            return;
        }

        // Create a print-friendly version
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Popup diblokir. Mohon izinkan popup untuk mencetak.');
            return;
        }

        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>${fileName}</title>
                <style>
                    @media print {
                        body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    }
                    body {
                        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                        padding: 20px;
                        line-height: 1.6;
                        color: #1a1a1a;
                        max-width: 800px;
                        margin: 0 auto;
                    }
                    h1, h2, h3, h4, h5, h6 { color: #333; margin-top: 1em; }
                    table { border-collapse: collapse; width: 100%; margin: 1em 0; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f5f5f5; font-weight: bold; }
                    ul, ol { padding-left: 2em; }
                    .bg-teal-50, .bg-blue-50, .bg-green-50, .bg-purple-50 { background-color: #f0f9ff !important; }
                    img { max-width: 100%; height: auto; }
                </style>
            </head>
            <body>
                ${content.innerHTML}
            </body>
            </html>
        `;

        printWindow.document.write(printContent);
        printWindow.document.close();

        // Wait for content to load then print
        printWindow.onload = () => {
            printWindow.print();
            printWindow.onafterprint = () => printWindow.close();
        };
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileImport = (event) => {
        const file = event.target.files[0];
        if (file && file.type === 'application/json') {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const json = JSON.parse(e.target.result);
                    if (onImportJson) {
                        onImportJson(json);
                    }
                } catch (error) {
                    console.error("Error parsing JSON file:", error);
                    alert("Gagal memuat file JSON. Pastikan formatnya benar.");
                }
            };
            reader.readAsText(file);
        }
        event.target.value = null; // Reset input
    };

    const toggleFullscreen = () => {
        const elem = targetRef?.current;
        if (!elem) return;

        if (!document.fullscreenElement) {
            elem.requestFullscreen().catch(err => {
                alert(`Gagal masuk mode layar penuh: ${err.message}`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!document.fullscreenElement;
            setIsFullscreen(isCurrentlyFullscreen);

            // Kelola style untuk pengalaman layar penuh yang lebih baik
            if (targetRef?.current) {
                if (isCurrentlyFullscreen && document.fullscreenElement === targetRef.current) {
                    // Saat masuk mode layar penuh, pastikan elemen dapat di-scroll dan punya background/padding
                    targetRef.current.classList.add('bg-white', 'p-8', 'overflow-y-auto');
                } else {
                    // Saat keluar, hapus style yang ditambahkan khusus untuk mode layar penuh
                    targetRef.current.classList.remove('p-8', 'overflow-y-auto');
                }
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, [targetRef]);

    return (
        <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileImport}
                accept=".json"
                className="hidden"
            />
            <Tooltip text="Impor JSON">
                <button
                    onClick={handleImportClick}
                    className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full"
                >
                    <FileUp size={18} />
                </button>
            </Tooltip>
            {/* Export options available for all content types */}
            <Tooltip text="Ekspor ke Word">
                <button
                    onClick={() => handleExport('doc')}
                    className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-full transition-colors"
                >
                    <FileType2 size={18} />
                </button>
            </Tooltip>
            <Tooltip text="Ekspor ke PDF">
                <button
                    onClick={() => handleExport('pdf')}
                    className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-full transition-colors"
                >
                    <FileSpreadsheet size={18} />
                </button>
            </Tooltip>
            {/* Print button - available for all content types */}
            <Tooltip text="Cetak / Print to PDF">
                <button
                    onClick={handlePrint}
                    className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full"
                >
                    <Printer size={18} />
                </button>
            </Tooltip>
            <Tooltip text="Ekspor ke Teks">
                <button
                    onClick={() => handleExport('txt')}
                    className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full"
                >
                    <FileText size={18} />
                </button>
            </Tooltip>
            <Tooltip text="Ekspor ke JSON">
                <button
                    onClick={() => handleExport('json')}
                    className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full"
                >
                    <FileJson size={18} />
                </button>
            </Tooltip>
            <div className="w-px h-6 bg-gray-300 mx-1"></div>
            <Tooltip text="Perkecil Huruf">
                <button
                    onClick={() => setFontSize(s => Math.max(8, s - 1))}
                    className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full"
                >
                    <ZoomOut size={18} />
                </button>
            </Tooltip>
            <Tooltip text="Perbesar Huruf">
                <button
                    onClick={() => setFontSize(s => Math.min(24, s + 1))}
                    className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full"
                >
                    <ZoomIn size={18} />
                </button>
            </Tooltip>
            <Tooltip text={isFullscreen ? "Keluar Layar Penuh" : "Layar Penuh"}>
                <button
                    onClick={toggleFullscreen}
                    className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full"
                >
                    {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                </button>
            </Tooltip>
        </div>
    );
};

export default OutputToolbar;

